import { mintSubscribeTicket } from '@/apiCalls/sse';

import { EEvents } from './EEvents';

const SSE_SUBSCRIPTION_BASE_URL =
    process.env.REACT_APP_SSE_SUBSCRIPTION_BASE_URL;
const SERVICE = process.env.REACT_APP_SSE_SERVICE_NAME;
const ENVIRONMENT = process.env.REACT_APP_SSE_ENV_NAME;

type openCallback = (event: Event) => void;
type messageCallback<T> = (message: T) => void;
type errorCallback = (error: unknown) => void;

/** What the caller gets back. Only `close()` — the subscription owns its own reconnects. */
export interface ISseSubscription {
    close: () => void;
}

/** Retry shape. Jittered so a relay restart does not produce a synchronised stampede. */
const MAX_ATTEMPTS = 6;
const BASE_DELAY_MS = 2000;
const MAX_DELAY_MS = 30000;

/**
 * How long a stream must stay open before it counts as "working".
 *
 * The retry counter is reset only after this, NOT on `onopen`. The relay's churn
 * measurements found 139,433 connect attempts in one day against ~580 steady
 * connections, because a client that resets its backoff on every successful open
 * never actually backs off — an open-drop-retry loop looks like a healthy
 * connection to `onopen`.
 */
const STABLE_MS = 30000;

const backoffDelay = (attempt: number): number => {
    const delay = Math.min(BASE_DELAY_MS * Math.pow(2, attempt), MAX_DELAY_MS);
    return delay * (0.75 + Math.random() * 0.5);
};

/**
 * Subscribe to an event update stream for the signed-in borrower.
 *
 * The borrower is NOT a parameter. It used to be — the caller decoded its own
 * JWT, pulled `borrowerId` out and put it in the URL, and the relay believed it.
 * That meant a borrower id was the only thing standing between an attacker and
 * somebody else's document notifications. Now the API mints a short-lived ticket
 * against the session, the relay takes the fan-out key from the ticket's `sub`,
 * and the id in the path is routing decoration the relay ignores.
 *
 * @param {EEvents} event Required - the event to subscribe to
 * @param {messageCallback<T>} messageCallback Required - callback to handle updates
 * @param {errorCallback} errorCallback Optional - called when the stream is given up on
 * @param {openCallback} openCallback Optional - callback to handle connection initiation
 * @returns a handle whose `close()` tears the subscription down for good
 *
 * @example const subscription = subscribeToSseEvent(EEvents.DOCUMENT_UPLOAD, (message) => console.log(message));
 *
 * @author Danyyil
 * @date 2023-10-23
 */
export function subscribeToSseEvent(
    event: EEvents,
    messageCallback: messageCallback<Record<string, string>>,
    errorCallback?: errorCallback,
    openCallback?: openCallback
): ISseSubscription {
    let eventSource: EventSource | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let stableTimer: ReturnType<typeof setTimeout> | null = null;
    let attempt = 0;
    let closed = false;

    const clearTimers = (): void => {
        if (retryTimer) clearTimeout(retryTimer);
        if (stableTimer) clearTimeout(stableTimer);
        retryTimer = null;
        stableTimer = null;
    };

    const teardown = (): void => {
        clearTimers();
        eventSource?.close();
        eventSource = null;
    };

    const retry = (reason: unknown): void => {
        if (closed) return;
        teardown();

        if (attempt >= MAX_ATTEMPTS) {
            errorCallback?.(reason);
            return;
        }

        const delay = backoffDelay(attempt);
        attempt += 1;
        retryTimer = setTimeout(() => {
            void open();
        }, delay);
    };

    const open = async (): Promise<void> => {
        if (closed) return;

        let subscription;
        try {
            // A fresh ticket EVERY time. The relay spends it at connect and never
            // looks at it again, so a reconnect cannot reuse the old one — that
            // is a 401 the browser's own retry loop would hide.
            subscription = await mintSubscribeTicket();
        } catch (error) {
            // A 401 here means the session is gone; the shared axios interceptor
            // has already cleared the token and raised the login modal. Retrying
            // is still right for the network-blip case, and harmless otherwise.
            retry(error);
            return;
        }

        if (closed) return;

        const url =
            `${SSE_SUBSCRIPTION_BASE_URL}/${SERVICE}/${ENVIRONMENT}/${event}/${subscription.subject}` +
            `?ticket=${encodeURIComponent(subscription.ticket)}`;

        eventSource = new EventSource(url);

        eventSource.onopen = (openEvent): void => {
            // Deliberately NOT `attempt = 0` here. See STABLE_MS.
            stableTimer = setTimeout(() => {
                attempt = 0;
            }, STABLE_MS);
            openCallback?.(openEvent);
        };

        eventSource.onmessage = (messageEvent): void => {
            messageCallback(JSON.parse(messageEvent.data));
        };

        // The relay ends the stream when the issuing session expires, using a
        // NAMED event so it can never be mistaken for an application payload by
        // a handler listening on `onmessage`.
        eventSource.addEventListener('session-expired', () => {
            retry(new Error('session-expired'));
        });

        eventSource.onerror = (error): void => {
            retry(error);
        };
    };

    void open();

    return {
        close: (): void => {
            closed = true;
            teardown();
        },
    };
}
